import type { TaskFlowRequest } from '@/lib/task-flow/taskFlowStore'

export type ExpressScriptJobStatus =
  | 'awaiting_product'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'

export type ExpressSelectedIdea = {
  title: string
  description: string
  format: string
  difficulty: string
  viral_score: number
  hook_suggestion: string
}

export type ExpressSelectedHook = {
  text: string
  type: string
  why_it_works: string
  score: number
}

export function buildExpressSelectedIdea(plan: any): ExpressSelectedIdea {
  return {
    title: String(
      plan?.title ||
        plan?.topic ||
        plan?.objective ||
        'Idea de contenido'
    ).trim(),

    description: String(
      plan?.description ||
        plan?.reasoning ||
        plan?.objective ||
        ''
    ).trim(),

    format: String(
      plan?.format ||
        plan?.platform ||
        'reel'
    )
      .trim()
      .toLowerCase(),

    difficulty: 'medio',

    viral_score: Math.max(
      1,
      Math.min(10, Number(plan?.viral_score || 8))
    ),

    hook_suggestion: String(plan?.hook || '').trim(),
  }
}

export function buildExpressSelectedHook(plan: any): ExpressSelectedHook {
  return {
    text: String(
      plan?.hook ||
        plan?.opening ||
        plan?.title ||
        ''
    ).trim(),

    type: String(plan?.hookType || 'curiosidad')
      .trim()
      .toLowerCase(),

    why_it_works: String(
      plan?.hookReason ||
        plan?.reasoning ||
        'Conecta directamente con el ángulo creativo aprobado.'
    ).trim(),

    score: Math.max(
      1,
      Math.min(10, Number(plan?.hookScore || 9))
    ),
  }
}

function resolveProjectType(direction: any, plan: any) {
  const goal = String(
    direction?.contentGoal ||
      plan?.goal ||
      ''
  ).toLowerCase()

  const publishTarget = String(
    direction?.publishTarget ||
      plan?.platform ||
      ''
  ).toLowerCase()

  if (goal === 'ugc') return 'ugc_avatar_video'
  if (goal === 'product') return 'product_ad'
  if (publishTarget === 'youtube') return 'youtube_video'

  return 'content_pipeline'
}

export async function createExpressContentProject(params: {
  request: TaskFlowRequest
  plan: any
  direction: any
  discovery: any
}) {
  const { request, plan, direction, discovery } = params

  const brief = {
    ...(plan || {}),

    title:
      plan?.title ||
      'Nuevo proyecto creativo',

    objective:
      plan?.objective ||
      discovery?.objective ||
      '',

    hook:
      plan?.hook ||
      '',

    cta:
      plan?.cta ||
      '',

    productName:
      plan?.productName ||
      '',

    productImageUrl:
      plan?.productImageUrl ||
      direction?.productImageUrl ||
      null,

    hasProduct:
      direction?.hasProduct ||
      plan?.hasProduct ||
      'no',

    hasProductImage:
      direction?.hasProductImage ||
      plan?.hasProductImage ||
      'no',

    useCharacter:
      direction?.useCharacter ||
      plan?.useCharacter ||
      'no',

    audience:
      plan?.audience ||
      discovery?.audience ||
      '',

    language:
      plan?.language ||
      'es',

    aspectRatio:
      plan?.aspectRatio ||
      (
        direction?.publishTarget === 'youtube'
          ? '16:9'
          : '9:16'
      ),

    durationMode:
      direction?.durationMode ||
      plan?.durationMode ||
      'auto',

    durationTargetSec: Number(
      direction?.durationTargetSec ||
        plan?.durationTargetSec ||
        60
    ),

    productionMode:
      direction?.productionMode ||
      plan?.productionMode ||
      'express',

    visualOutputMode:
      direction?.visualOutputMode ||
      plan?.visualOutputMode ||
      'mixed',

    goal:
      plan?.goal ||
      direction?.contentGoal ||
      'viral',

    style:
      plan?.style ||
      '',

    musicMood:
      plan?.musicMood ||
      '',

    voiceStyle:
      plan?.voiceStyle ||
      '',

    discovery,
  }

  const res = await fetch('/api/creator-studio/projects', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      sellerId: request.payload?.sellerId,

      type: resolveProjectType(direction, plan),

      mode: 'auto',

      title:
        brief.title ||
        'Nuevo proyecto creativo',

      objective:
        brief.objective ||
        '',

      currentStep: 'scripts',

      brief,

      creativeDirection: direction,

      projectJson: null,

      skillAgentId:
        request.payload?.skillAgentId ||
        request.payload?.skill_agent_id ||
        null,

      skillAgentSlug:
        request.payload?.skillAgentSlug ||
        request.payload?.skill_agent_slug ||
        null,

      skillAgentName:
        request.payload?.skillName ||
        request.payload?.skillAgentName ||
        request.payload?.skill_agent_name ||
        null,
    }),
  })

  const json = await res.json().catch(() => null)

  if (!res.ok || !json?.ok || !json?.projectId) {
    throw new Error(
      json?.error ||
        'No se pudo crear el proyecto express.'
    )
  }

  return {
    projectId: Number(json.projectId),
    project: json.project || null,
    brief,
  }
}

export async function updateExpressProject(params: {
  projectId: number
  currentStep?: string
  brief?: any
  projectJson?: any
}) {
  const res = await fetch(
    `/api/creator-studio/projects/${params.projectId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        currentStep: params.currentStep,
        brief: params.brief,
        projectJson: params.projectJson,
      }),
    }
  )

  const json = await res.json().catch(() => null)

  if (!res.ok || !json?.ok) {
    throw new Error(
      json?.error ||
        'No se pudo actualizar el proyecto.'
    )
  }

  return json
}

export async function startExpressScriptJob(inputs: any) {
  const res = await fetch(
    '/api/creator-studio/jobs/scripts/start',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        inputs,
      }),
    }
  )

  const json = await res.json().catch(() => null)

  if (!res.ok || !json?.ok || !json?.jobId) {
    throw new Error(
      json?.error ||
        json?.message ||
        'No se pudo iniciar el guion.'
    )
  }

  return {
    jobId: Number(json.jobId),
    status: String(json.status || 'queued'),
    progress: Number(json.progress || 0),
    step: json.step || 'Queued',
    reused: Boolean(json.reused),
    resultGenerationId:
      json.resultGenerationId
        ? Number(json.resultGenerationId)
        : null,
  }
}

export async function getExpressScriptJob(jobId: number) {
  const res = await fetch(
    `/api/creator-studio/jobs/${jobId}`,
    {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
    }
  )

  const json = await res.json().catch(() => null)

  if (!res.ok || !json?.ok) {
    throw new Error(
      json?.error ||
        'No se pudo consultar el job.'
    )
  }

  return json
}

export async function saveExpressContentSnapshot(params: {
  snapshotId?: number | null
  sellerId?: number | string | null
  generationId?: number | null
  phase: string
  request: TaskFlowRequest
  result: any
}) {
  const res = await fetch(
    '/api/task-flows/create-content/snapshot',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        snapshotId:
          params.snapshotId || null,

        sellerId:
          params.sellerId || null,

        generationId:
          params.generationId || null,

        phase:
          params.phase,

        snapshot: {
          phase:
            params.phase,

          request: {
            ...params.request,
            phase:
              params.phase,
          },

          result:
            params.result,
        },
      }),
    }
  )

  const json = await res.json().catch(() => null)

  if (!res.ok || !json?.ok) {
    throw new Error(
      json?.error ||
        'No se pudo guardar el avance express.'
    )
  }

  return Number(
    json.snapshotId ||
      params.snapshotId ||
      0
  )
}